import {
  ApplicantData,
  BookingConfigurationResponse,
  CreateSubmissionRequest,
  CreateSubmissionResponse,
  LeadPrivacyRequestBody,
  ProspectData,
  SavedLeadInfo,
  SavedSubmissionRecord,
  SubmissionByProspectResponse,
  SubmissionByReserveIdResponse,
  UnsubscribeRequestBody,
} from '@/types/salesforce';
import { isServer } from '@/utils/isServer';
import type { ConvertProspectOptions } from '@/utils/prospectConversion';
import type { ConvertPennieOptions } from '@/utils/pennieConversion';
import type { ResubmitOptions } from '@/utils/resubmission';
import { InternalSalesforceService } from '../InternalServices/InternalSalesforceService';
import { internalApi } from '../axiosConfig/internalApi';
import { withoutSensitive } from '../redactSensitive';

export class SalesforceService {
  static async createSubmission(data: CreateSubmissionRequest) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.createSubmission(data);
      return response;
    }
    const response = await internalApi.post<CreateSubmissionResponse>(
      '/api/salesforce/create-submission',
      data
    );
    return response;
  }

  static async updateSubmission(
    id: string,
    data: Partial<CreateSubmissionRequest>
  ) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.updateSubmission(
        id,
        data
      );
      return response;
    }
    const response = await internalApi.patch(
      `/api/salesforce/update-submission/${id}`,
      data
    );
    return response;
  }

  static async partialUpdateSubmission(
    id: string,
    data: Partial<CreateSubmissionRequest>
  ) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.partialUpdateSubmission(
        id,
        data
      );
      return response;
    }
    const response = await internalApi.patch(
      `/api/salesforce/partial-update-submission/${id}`,
      data
    );
    return response;
  }

  static async getSavedSubmission(id: string, { resume = false } = {}) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.getSavedSubmission(id);
      return { ...response, data: withoutSensitive(response.data) };
    }
    const response = await internalApi.get<SavedSubmissionRecord>(
      `/api/salesforce/get-saved-submission/${id}`,
      resume ? { params: { resume: 1 } } : undefined
    );
    return response;
  }

  static async unsubscribe(data: UnsubscribeRequestBody) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.unsubscribe(data);
      return response;
    }
    const response = await internalApi.patch<string>(
      '/api/salesforce/unsubscribe',
      data
    );
    return response;
  }

  static async logSubmissionCreating(data: {
    Submission__c: string;
    RawJSON__c: string;
  }) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response =
        await InternalSalesforceService.logSubmissionCreating(data);
      return response;
    }
    const response = await internalApi.post<{
      id: string;
      success: true;
      errors: [];
    }>('/api/salesforce/log-submission-creating', data);
    return response;
  }

  static async updateLeadPrivacy(data: LeadPrivacyRequestBody) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.updateLeadPrivacy(data);
      return response;
    }
    const response = await internalApi.post<{
      message: string;
      statusCode: string;
      leadId: string;
    }>('/api/salesforce/update-lead-privacy', data);
    return response;
  }

  static async getLeadInfo(leadId: string) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.getLeadInfo(leadId);
      return { ...response, data: withoutSensitive(response.data) };
    }
    const response = await internalApi.get<SavedLeadInfo>(
      `/api/salesforce/get-lead-info/${leadId}`
    );
    return response;
  }

  static async updateLeadInfo(id: string, data: Partial<SavedLeadInfo>) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.updateLeadInfo(id, data);
      return response;
    }
    const response = await internalApi.patch(
      `/api/salesforce/update-lead/${id}`,
      data
    );
    return response;
  }

  // Server only.
  static async searchAgentName(email: string) {
    return InternalSalesforceService.searchAgentName(email);
  }
  static async assignReferralAgent(submissionId: string, mail: string) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.assignReferralAgent(
        submissionId,
        mail
      );
      return response;
    }
    const response = await internalApi.patch(
      `/api/salesforce/assign-referral-agent/${submissionId}`,
      { Regal_Assigned_Agent__c: mail }
    );
    return response;
  }

  static async getProspectRecord(id: string) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.getProspectRecord(id);
      return { ...response, data: withoutSensitive(response.data) };
    }
    const response = await internalApi.get<ProspectData>(
      `/api/salesforce/get-prospect-record/${id}`
    );
    return response;
  }

  static async updateProspect(id: string, data: Partial<ProspectData>) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.updateProspect(id, data);
      return response;
    }
    const response = await internalApi.patch(
      `/api/salesforce/update-prospect/${id}`,
      data
    );
    return response;
  }

  // Server only: browsers never read an applicant.
  static async getApplicantRecord(id: string) {
    const response = await InternalSalesforceService.getApplicantRecord(id);
    return { ...response, data: withoutSensitive(response.data) };
  }
  static async updateApplicant(id: string, data: Partial<ApplicantData>) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.updateApplicant(
        id,
        data
      );
      return response;
    }
    const response = await internalApi.patch(
      `/api/salesforce/update-applicant/${id}`,
      data
    );
    return response;
  }

  static async getSubmissionByReserveId(id: string) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response =
        await InternalSalesforceService.getSubmissionByReserveId(id);
      return response;
    }
    const response = await internalApi.get<SubmissionByReserveIdResponse>(
      `/api/salesforce/get-submission-by-reserve-id/${id}`
    );
    return response;
  }

  static async getBookingConfig() {
    const isServerSide = isServer();
    if (isServerSide) {
      const response = await InternalSalesforceService.getBookingConfig();
      return response;
    }
    const response = await internalApi.get<BookingConfigurationResponse>(
      '/api/salesforce/get-booking-config'
    );
    return response;
  }

  static async updateLeadCallStatus(submissionId: string) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response =
        await InternalSalesforceService.updateLeadCallStatus(submissionId);
      return response;
    }
    const response = await internalApi.patch(
      `/api/salesforce/update-lead-call-status/${submissionId}`
    );
    return response;
  }

  // Browser only: the booking page with a booking-only link token, or null.
  static async findBookingPath(
    email: string,
    zipCode: string
  ): Promise<string | null> {
    const response = await internalApi.post<{
      submissionId: string | null;
      token: string | null;
    }>('/api/salesforce/find-submission-id', { email, zipCode });
    const { submissionId, token } = response.data;
    if (!submissionId) return null;
    return token
      ? `/booking/${submissionId}?t=${encodeURIComponent(token)}`
      : `/booking/${submissionId}`;
  }

  // Browser only. The submit-button conversions; auto conversions run while
  // the prospect's page renders.
  static async convertProspect(
    prospectId: string,
    options: Omit<ConvertProspectOptions, 'auto' | 'offerLandingPage'> & {
      variant: 'offerhub' | 'engine';
    }
  ): Promise<string | null> {
    const response = await internalApi.post<{ submissionId: string | null }>(
      `/api/salesforce/convert-prospect/${prospectId}`,
      options
    );
    return response.data.submissionId;
  }

  // Browser only. Copies a reserve submission into a Pennie one on the server.
  static async convertPennie(
    reserveId: string,
    options: Omit<ConvertPennieOptions, 'userAgent'>
  ) {
    const response = await internalApi.post<{ submissionId: string }>(
      `/api/salesforce/convert-pennie/${reserveId}`,
      options
    );
    return response.data.submissionId;
  }

  // Browser only. Copies an expired submission into a new one on the server.
  static async resubmit(
    submissionId: string,
    options: ResubmitOptions
  ): Promise<string> {
    const response = await internalApi.post<{ submissionId: string }>(
      `/api/salesforce/resubmit/${submissionId}`,
      options
    );
    return response.data.submissionId;
  }
  static async getSubmissionByProspect(id: string) {
    const isServerSide = isServer();
    if (isServerSide) {
      const response =
        await InternalSalesforceService.getSubmissionByProspect(id);
      return response;
    }
    const response = await internalApi.get<SubmissionByProspectResponse>(
      `/api/salesforce/get-submission-by-prospect/${id}`
    );
    return response;
  }
}
